import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Euler, MathUtils, Quaternion } from "three";
import { collisionDebugService } from "@modules/selection/collision/CollisionDebugService";
import { ColliderGeometryPreview } from "./ColliderGeometryPreview.component";
import "./CollisionTab.component.css";
function formatVector3(v) {
    return v.map((n) => n.toFixed(3)).join(", ");
}
function formatEulerDegrees(orientation) {
    const euler = new Euler().setFromQuaternion(new Quaternion(...orientation));
    return [euler.x, euler.y, euler.z].map((rad) => `${MathUtils.radToDeg(rad).toFixed(1)}°`).join(", ");
}
function ColliderDetail({ collider }) {
    return (React.createElement("div", { className: "collision-collider-detail" },
        React.createElement("div", { className: "collision-collider-stats" },
            React.createElement("div", { className: "collision-stat" },
                React.createElement("span", { className: "collision-stat-label" }, "Triangles"),
                React.createElement("span", null, collider.triCount)),
            React.createElement("div", { className: "collision-stat" },
                React.createElement("span", { className: "collision-stat-label" }, "Position"),
                React.createElement("span", null, formatVector3(collider.position))),
            React.createElement("div", { className: "collision-stat" },
                React.createElement("span", { className: "collision-stat-label" }, "Orientation"),
                React.createElement("span", null, formatEulerDegrees(collider.orientation))),
            React.createElement("div", { className: "collision-stat" },
                React.createElement("span", { className: "collision-stat-label" }, "Scale"),
                React.createElement("span", null, formatVector3(collider.scale))),
            React.createElement("div", { className: "collision-stat" },
                React.createElement("span", { className: "collision-stat-label" }, "Bounds tree"),
                React.createElement("span", { className: `collision-flag ${String(collider.hasBoundsTree)}` }, String(collider.hasBoundsTree))),
            React.createElement("div", { className: "collision-stat" },
                React.createElement("span", { className: "collision-stat-label" }, "Active for selection"),
                React.createElement("span", { className: `collision-flag ${String(collider.isActiveForSelection)}` }, String(collider.isActiveForSelection)))),
        React.createElement("div", { className: "collision-collider-preview" },
            React.createElement(ColliderGeometryPreview, { geometry: collider.geometry }))));
}
export function CollisionTab() {
    const [rooms, setRooms] = useState([]);
    const [selectedRoomUID, setSelectedRoomUID] = useState();
    const [expandedUID, setExpandedUID] = useState();
    useEffect(() => {
        const update = () => {
            const nextRooms = collisionDebugService.getSnapshot().rooms;
            setRooms(nextRooms);
            if (nextRooms.length === 0) {
                setSelectedRoomUID(undefined);
                return;
            }
            setSelectedRoomUID((current) => {
                var _a, _b;
                if (current !== undefined && nextRooms.some((room) => room.uid === current)) {
                    return current;
                }
                return (_b = (_a = nextRooms.find((room) => room.isActive)) === null || _a === void 0 ? void 0 : _a.uid) !== null && _b !== void 0 ? _b : nextRooms[0].uid;
            });
        };
        update();
        const timer = setInterval(update, 250);
        return () => { clearInterval(timer); };
    }, []);
    const selectedRoom = useMemo(() => rooms.find((room) => room.uid === selectedRoomUID), [rooms, selectedRoomUID]);
    const expandedCollider = useMemo(() => selectedRoom === null || selectedRoom === void 0 ? void 0 : selectedRoom.colliders.find((collider) => collider.uid === expandedUID), [selectedRoom, expandedUID]);
    return (React.createElement("div", { className: "collision-tab-container" },
        React.createElement("div", { className: "collision-rooms" }, rooms.map((room) => (React.createElement("div", { key: room.uid, className: `collision-room-row ${room.uid === selectedRoomUID ? "selected" : ""}`, onClick: () => { setSelectedRoomUID(room.uid); setExpandedUID(undefined); } },
            React.createElement("span", { className: `collision-room-name ${room.isActive ? "active" : ""}` }, room.roomName),
            React.createElement("span", { className: "collision-room-count" },
                "(",
                room.colliderCount,
                ")"))))),
        React.createElement("div", { className: "collision-models" },
            !selectedRoom && React.createElement("div", { className: "collision-empty" }, "No collision rooms"),
            selectedRoom && selectedRoom.colliders.length === 0 && (React.createElement("div", { className: "collision-empty" }, "No colliders in this room")), selectedRoom === null || selectedRoom === void 0 ? void 0 :
            selectedRoom.colliders.map((collider) => (React.createElement("div", { key: collider.uid, className: "collision-model-entry" },
                React.createElement("div", { className: `collision-model-row ${collider.uid === expandedUID ? "expanded" : ""}`, onClick: () => { setExpandedUID((current) => (current === collider.uid ? undefined : collider.uid)); } },
                    React.createElement("span", { className: "collision-model-name" }, collider.modelName),
                    React.createElement("span", { className: "collision-shape-type" }, collider.shapeType),
                    React.createElement("span", { className: `collision-flag ${String(collider.isTrigger)}` }, "isTrigger"),
                    React.createElement("span", { className: `collision-flag ${String(collider.isSelectable)}` }, "isSelectable")),
                collider.uid === expandedUID && expandedCollider && React.createElement(ColliderDetail, { collider: expandedCollider })))))));
}
